import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Vote, Check } from 'lucide-react';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { LanguageSelector } from '../components/LanguageSelector';
import { Player } from '../types';

interface CategoryVotingPageProps {
  players: Player[];
  currentPlayer: Player;
  categoryVotes: Record<string, string>;
  onBack: () => void;
  onVoteCategory: (category: string) => void;
}

const categories = [
  { id: 'animales', name: 'Animales', emoji: '🐾' },
  { id: 'comida', name: 'Comida', emoji: '🍔' },
  { id: 'objetos', name: 'Objetos', emoji: '📦' },
  { id: 'películas', name: 'Películas', emoji: '🎬' },
  { id: 'lugares', name: 'Lugares', emoji: '🌍' },
  { id: 'profesiones', name: 'Profesiones', emoji: '💼' },
];

export const CategoryVotingPage: React.FC<CategoryVotingPageProps> = ({
  players,
  currentPlayer,
  categoryVotes,
  onBack,
  onVoteCategory,
}) => {
  const { t } = useTranslation();
  const [selectedCategory, setSelectedCategory] = useState<string | null>(
    categoryVotes[currentPlayer.id] || null
  );

  const hasVoted = categoryVotes[currentPlayer.id] !== undefined;
  const totalVotes = Object.keys(categoryVotes).length;
  const totalPlayers = players.filter(p => p.isConnected).length;

  const handleVote = () => {
    if (selectedCategory) {
      onVoteCategory(selectedCategory);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 flex flex-col items-center justify-center p-4 relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%239C92AC%22%20fill-opacity%3D%220.1%22%3E%3Ccircle%20cx%3D%2230%22%20cy%3D%2230%22%20r%3D%222%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-20 pointer-events-none"></div>
      
      {/* Language Selector */}
      <div className="absolute top-4 right-4 z-10">
        <LanguageSelector />
      </div>

      {/* Back Button */}
      <div className="absolute top-4 left-4 z-10">
        <Button
          variant="secondary"
          size="sm"
          onClick={onBack}
          icon={<ArrowLeft className="w-4 h-4" />}
        >
          Volver
        </Button>
      </div>

      {/* Main Content */}
      <div className="w-full max-w-4xl animate-fade-in">
        <div className="text-center mb-8">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 font-display">
            🗳️ Votación de Categoría
          </h1>
          <p className="text-xl text-white/80">
            Selecciona la categoría que quieres jugar
          </p>
        </div>

        {/* Voting Progress */}
        <Card className="mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Vote className="w-6 h-6 text-white" />
              <span className="text-lg text-white">
                Votos: {totalVotes} / {totalPlayers}
              </span>
            </div>
            {hasVoted && (
              <div className="flex items-center gap-2 text-success-400">
                <Check className="w-5 h-5" />
                <span className="font-medium">¡Has votado!</span>
              </div>
            )}
          </div>
          <div className="w-full bg-white/10 rounded-full h-2 mt-4">
            <div
              className="bg-gradient-to-r from-primary-500 to-primary-600 h-2 rounded-full transition-all duration-500"
              style={{ width: `${(totalVotes / totalPlayers) * 100}%` }}
            />
          </div>
        </Card>

        {/* Categories Grid */}
        <Card className="mb-6">
          <h2 className="text-2xl font-bold text-white mb-6">Categorías Disponibles</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {categories.map((category) => {
              const voteCount = Object.values(categoryVotes).filter(v => v === category.id).length;
              const isSelected = selectedCategory === category.id;
              
              return (
                <button
                  key={category.id}
                  onClick={() => !hasVoted && setSelectedCategory(category.id)}
                  disabled={hasVoted}
                  className={`p-6 rounded-xl border-2 transition-all duration-200 ${
                    isSelected
                      ? 'border-primary-500 bg-primary-500/20 scale-105'
                      : hasVoted
                      ? 'border-white/20 bg-white/5 opacity-50 cursor-not-allowed'
                      : 'border-white/30 bg-white/10 hover:border-primary-400 hover:bg-primary-500/10'
                  }`}
                >
                  <div className="text-5xl mb-3">{category.emoji}</div>
                  <div className="text-lg font-bold text-white mb-2">{category.name}</div>
                  {voteCount > 0 && (
                    <div className="text-sm text-white/60">
                      {voteCount} {voteCount === 1 ? 'voto' : 'votos'}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </Card>

        {/* Vote Button */}
        {!hasVoted && (
          <div className="flex justify-center">
            <Button
              variant="primary"
              size="lg"
              onClick={handleVote}
              disabled={!selectedCategory}
              icon={<Vote className="w-5 h-5" />}
            >
              Confirmar Voto
            </Button>
          </div>
        )}

        {hasVoted && (
          <div className="text-center">
            <p className="text-white/80">
              Esperando a que los demás jugadores voten...
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
